import { Box, Divider, Grid } from '@mui/material'
import React from 'react'
import {
  EditButton,
  FunctionField,
  NumberField,
  ReferenceField,
  ResourceContextProvider,
  Show,
  ShowProps,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TextField,
  useShowContext,
  useTranslate,
  TabbedShowLayoutTabs,
} from 'react-admin'
import { ResourceName } from '../../core/ResourceName'
import { Authority } from '../../core/auth/Authority'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { PrintLayoutDto } from '../../core/dto/print-layout.dto'
import { FilePathDto } from '../../core/dto/files/FilesPath.dto'
import { PDFPrinterDefinitionsDto } from '../../core/dto/pdf-printer-definitions.dto'
import { PrintTextItemsList } from './print-layouts-show/PrintTextItemsList'
import { PrintBarcodeItemsList } from './print-layouts-show/PrintBarcodeItemsList'
import { PrintQRCodeItemsList } from './print-layouts-show/PrintQRCodeItemsList'

const ShowTitle = (props: { record?: PrintLayoutDto }) => {
  const { record: printLayout } = props
  const translate = useTranslate()

  return (
    <span>
      {translate('resources.print-layouts.titles.show')}: {printLayout?.id}.{' '}
      {printLayout?.name}
    </span>
  )
}

const GeneralActions = () => {
  const { record: template } = useShowContext<PrintLayoutDto>()
  const hasAuthority = useHasAuthority()
  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item style={{ margin: '2px' }}>
            <EditButton
              basePath={`/${ResourceName.PRINT_LAYOUTS}`}
              record={template}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_PRINT_LAYOUTS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const PrintItemsTab = ({ ...props }) => {
  const { record: printLayout } = useShowContext()

  return (
    <Tab {...props} label="resources.print-layouts.tabs.print-text-items">
      <SimpleShowLayout {...props}>
        <ResourceContextProvider value={ResourceName.PRINT_TEXT_ITEMS}>
          <PrintTextItemsList
            title=" "
            filter={{ printLayoutId: printLayout?.id }}
          />
        </ResourceContextProvider>
      </SimpleShowLayout>
    </Tab>
  )
}

const PrintBarcodesTab = ({ ...props }) => {
  const { record: printLayout } = useShowContext()

  return (
    <Tab {...props} label="resources.print-layouts.tabs.print-barcode-items">
      <SimpleShowLayout {...props}>
        <ResourceContextProvider value={ResourceName.PRINT_BARCODE_ITEMS}>
          <PrintBarcodeItemsList
            title=" "
            filter={{ printLayoutId: printLayout?.id }}
          />
        </ResourceContextProvider>
      </SimpleShowLayout>
    </Tab>
  )
}

const PrintQRCodesTab = ({ ...props }) => {
  const { record: printLayout } = useShowContext()

  return (
    <Tab {...props} label="resources.print-layouts.tabs.print-qrcode-items">
      <SimpleShowLayout {...props}>
        <ResourceContextProvider value={ResourceName.PRINT_QRCODE_ITEMS}>
          <PrintQRCodeItemsList
            title=" "
            filter={{ printLayoutId: printLayout?.id }}
          />
        </ResourceContextProvider>
      </SimpleShowLayout>
    </Tab>
  )
}

const GeneralTab = ({ ...props }) => {
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  return (
    <Tab {...props} label="resources.print-layouts.tabs.general">
      <SimpleShowLayout {...props}>
        <TextField source="id" label="Id" />
        <TextField source="name" />
        <NumberField
          source="width"
          label={`${translate('resources.print-layouts.fields.width')} (mm)`}
        />
        <NumberField
          source="height"
          label={`${translate('resources.print-layouts.fields.height')} (mm)`}
        />
        {hasAuthority(Authority.VIEW_FILES) && (
          <ReferenceField
            source="backgroundImageId"
            reference={ResourceName.FILES}
            link={false}
          >
            <FunctionField<FilePathDto>
              render={(record?: FilePathDto) =>
                `${record?.id}. ${record?.name}`
              }
            />
          </ReferenceField>
        )}
        {hasAuthority(Authority.VIEW_PDF_PRINTER_DEFINITIONS) && (
          <ReferenceField
            source="printerDefinitionId"
            reference={ResourceName.PDF_PRINTER_DEFINITIONS}
            link={false}
          >
            <FunctionField<PDFPrinterDefinitionsDto>
              render={(record?: PDFPrinterDefinitionsDto) =>
                `${record?.id}. ${record?.name}`
              }
            />
          </ReferenceField>
        )}
      </SimpleShowLayout>
      <GeneralActions />
    </Tab>
  )
}

const PrintLayoutTabs = ({ ...props }) => (
  <TabbedShowLayout
    {...props}
    tabs={
      <TabbedShowLayoutTabs
        variant="scrollable"
        scrollButtons="auto"
        {...props}
      />
    }
  >
    <GeneralTab />
    <PrintItemsTab path="items" />
    <PrintBarcodesTab path="barcodes" />
    <PrintQRCodesTab path="qrcodes" />
  </TabbedShowLayout>
)

export const PrintLayoutShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <PrintLayoutTabs />
  </Show>
)
